import React from "react";
import styled from "styled-components";
import { Button } from "../../general/Button";
import { Typography } from "../../general/Typography";
import { Flex } from "../../layouts";

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSizeOptions?: number[];
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
}

const PaginationWrapper = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageButtons = styled(Flex)`
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`;

const PageButton = styled(Button) <{ active?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.radii.lg};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  ${({ active, theme }) =>
    active
      ? `
    background: ${theme.colors.primary};
    color: ${theme.colors.textonPrimary};
  `
      : `
    background: ${theme.colors.surface};
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.border};
  `}
`;

const PageSizeSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
`;

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSizeOptions = [5, 10, 25],
  pageSize,
  onPageSizeChange,
}) => {
  const createPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) end = Math.min(totalPages, maxVisible);
    if (currentPage > totalPages - 3) start = Math.max(1, totalPages - (maxVisible - 1));

    if (start > 1) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) pages.push("...");

    return pages;
  };

  return (
    <PaginationWrapper>
      <PageButtons>
        {createPageNumbers().map((page, idx) =>
          page === "..." ? (
            <Typography key={`dots-${idx}`} variant="caption">...</Typography>
          ) : (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(Number(page))}
            >
              {page}
            </PageButton>
          )
        )}
      </PageButtons>

      {pageSize && onPageSizeChange && (
        <PageSizeSelect
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </PageSizeSelect>
      )}
    </PaginationWrapper>
  );
};